import React, {useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
    IonButtons,
    IonHeader,
    IonPage, IonToast,
    useIonViewDidEnter
} from '@ionic/react';
import {StyledIonContent, StyledIonTitle, StyledIonToolbar} from '@components/content/content.style';
import {StyledHeaderButton, StyledHeaderButtonImage} from '@components/button/button.style';
import ArrowLeftImage from '../../assets/images/arrow-left.svg';
import CheckImage from '../../assets/images/check.svg';
import {useForm} from 'react-hook-form';
import {StyledContent} from './addEditPhonePage.style';
import {getConfiguration} from '@services/addEditValuePage.service';
import {Links} from '../links';
import Form from '../../components/form';
import StaticPane from '../../components/pane/static-pane.component';
import {useTranslation} from "react-i18next";
import 'react-phone-input-2/lib/style.css'
import ExactPhoneInput from '../../components/form/input/phoneInput.component';
import SignModal from "@modals/sign/sign.component";
import {updatePhoneFieldValue} from "@services/profile.service";
import {Preferences} from "@capacitor/preferences";
import SaveConfirmationModal from "@app/myData/modals/saveConfirmationModal.component";
import SignTypeSelectModal from "@modals/sign/signTypeSelect.component";
import {SignType} from "@modals/sign/sign.types";

const AddEditPhonePage: React.FC = () => {
    const {t} = useTranslation();
    const history = useHistory();

    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [submitted, setSubmitted] = useState(false);
    const [err, setErr] = useState('');
    const [startSaving, setStartSaving] = useState(false);

    const [showSignTypeSelectModal, setShowSignTypeSelectModal] = useState<boolean>(false);
    const [showSignModal, setShowSignModal] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toast, setToast] = useState<string>('');
    const [showSaveConfirmationModal, setShowSaveConfirmationModal] = useState<boolean>(false);

    const [signType, setSignType] = useState<SignType>();
    const [defaultPhoneNumber, setDefaultPhoneNumber] = useState<string>('');
    const [phoneTmpVal, setPhoneTmpVal] = useState<string>('');
    const [phoneNumber, updatePhoneNumber] = useState(getConfiguration()?.defaultValue);

    const header = useRef<HTMLIonHeaderElement>(null);

    const invalidPhoneError = t("profileTab.personalData.validation.invalidPhone");
    const invalidPhoneErrorPl = t("profileTab.personalData.validation.pl.invalidPhone");
    const phoneNumberName = "phoneNumber";

    const {setValue, handleSubmit, errors} = useForm({mode: 'onChange'});

    useEffect(() => {
        const defaultValue = getConfiguration()?.defaultValue;

        setValue(phoneNumberName, defaultValue);

        if (defaultValue) {
            setDefaultPhoneNumber(defaultValue.replace(/\+/g, ''));
        }
    }, []);

    useEffect(() => {
        updateHeight();
    });

    useEffect(() => {
        if (signType) {
            handleSave();
        }
    }, [signType]);

    useIonViewDidEnter(() => {
        document.getElementsByName(phoneNumberName)[0].focus();
    });

    if (!getConfiguration()) {
        history.goBack();
    }

    const handleBack = () => {
        const handleDismiss = getConfiguration()?.handleDismiss;

        if (typeof handleDismiss === 'function') {
            handleDismiss();
        } else {
            getConfiguration()?.url ? history.replace(getConfiguration()?.url || Links.login, history.location.state) : history.goBack();
        }
    };

    const handleSave = async () => {
        if (err || errors.field || !phoneNumber) {
            setStartSaving(true);
            return;
        }

        const handleSave = getConfiguration()?.handleSave;

        if (typeof handleSave === 'function') {
            setSubmitted(true);

            if (!submitted) {
                let response: any = await handleSave(phoneNumber, signType);

                setSubmitted(false);

                if (typeof response === 'object') {
                    setPhoneTmpVal(phoneNumber);
                    setShowSignModal(true);
                } else {
                    setErr(response);
                    setStartSaving(true);
                }
            }
        } else {
            setSubmitted(false);
            getConfiguration()?.url ? history.replace(getConfiguration()?.url || Links.login, history.location.state) : history.goBack();
        }
    };

    const handleShowSaveConfirmationModal = () => {
        if (err || errors.field || !phoneNumber) {
            setStartSaving(true);
            return;
        }

        setShowSaveConfirmationModal(true);
    }

    const updateHeight = () => {
        if (header.current?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    const handlePhoneErr = (error: boolean, code: string) => {
        if (error) {
            if (code != '48') {
                setErr(invalidPhoneError);
            } else {
                setErr(invalidPhoneErrorPl);
            }
        } else {
            setErr('');
        }
    }

    const signSave = async (code: string) => {
        await updatePhoneFieldValue(phoneTmpVal, code)
            .then(response => {
                updateStorage('phone', phoneTmpVal);
                setShowSignModal(false);

                const redirectTo = getConfiguration()?.redirectTo;

                if (redirectTo) {
                    history.push(redirectTo + '?openModal=true');
                } else {
                    history.goBack();
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 400) {
                    setToast(t("common.invalidVerifyCode"));
                    setShowToast(true);
                }
            });
    };

    const updateStorage = async (field: string, value: any) => {
        field = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

        await Preferences.set({
            'key': field,
            'value': value
        });
    }

    return (
        <IonPage>
            <IonToast
                isOpen={showToast}
                onDidDismiss={() => setShowToast(false)}
                message={toast}
                duration={6000}
                position="top"
                color="danger"
            />
            <StyledIonContent>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <IonButtons slot="start">
                            <StyledHeaderButton onClick={() => handleBack()}>
                                <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                        <StyledIonTitle>{getConfiguration()?.title}</StyledIonTitle>
                        <IonButtons slot="end">
                            <StyledHeaderButton onClick={handleShowSaveConfirmationModal}>
                                <StyledHeaderButtonImage src={CheckImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                    </StyledIonToolbar>
                </IonHeader>
                <StaticPane topEdge={topEdge} marginTop={40} hideGrabber={true}>
                    <Form.Container onSubmit={handleSubmit(handleShowSaveConfirmationModal)}>
                        <StyledContent>
                            <ExactPhoneInput
                                name={phoneNumberName}
                                phoneNumer={phoneNumber || '48 '}
                                phoneNumerChanged={(phoneNumber) => {
                                    updatePhoneNumber(phoneNumber)
                                }}
                                errorChanged={handlePhoneErr}
                            ></ExactPhoneInput>
                            {startSaving && err && <p className="errorMessage">{err}</p>}
                        </StyledContent>
                    </Form.Container>
                </StaticPane>
            </StyledIonContent>
            <SaveConfirmationModal
                isOpen={showSaveConfirmationModal}
                onConfirm={() => setShowSignTypeSelectModal(true)}
                onCancel={(() => setShowSaveConfirmationModal(false))}
            />
            <SignTypeSelectModal
                isOpen={showSignTypeSelectModal}
                onClose={() => setShowSignTypeSelectModal(false)}
                onSelect={(signType) => setSignType(signType)}
            />
            <SignModal
                configuration={{
                    title: t("personalDataPhonePage.sign.title"),
                    info: t("personalDataPhonePage.sign.info"),
                    buttonText: t("personalDataPhonePage.sign.signButton"),
                    phone: defaultPhoneNumber || phoneTmpVal,
                    signType: signType,
                }}
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                onSave={(code: string) => signSave(code)}
            />
        </IonPage>
    );
};

export default AddEditPhonePage;
